"use client";

import React from "react";
import { MapPin, Network, Shield, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MpwJawaBaratStructure() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const keyRoles = [{
    icon: <Shield className="size-5 text-primary" />,
    title: "Komando Wilayah",
    desc: "Pusat koordinasi dan instruksi untuk seluruh cabang di Jawa Barat."
  }, {
    icon: <Users className="size-5 text-primary" />,
    title: "Pembinaan Kader",
    desc: "Fokus pada pengembangan kapasitas dan kualitas anggota daerah."
  }];
  return <section id="wilayah" className="py-16 md:py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-24 text-center max-w-3xl mx-auto transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-6 text-primary border-primary/30 px-4 py-1 text-sm">
            Tingkat Provinsi
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 uppercase tracking-tight text-foreground">
            MPW Jawa Barat
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground font-default font-medium">
            Struktur Wilayah Jawa Barat
          </p>
          <Separator className="mt-8 max-w-[120px] mx-auto bg-primary/40 h-1 rounded-lg" />
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-semibold font-default flex items-center gap-3 text-foreground">
                <MapPin className="text-primary size-8" />
                Hierarki Regional
              </h3>
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed font-sans">
                <p>
                  Majelis Pimpinan Wilayah (MPW) Jawa Barat merupakan tingkat komando provinsi yang memegang peranan strategis dalam membawahi dan mengkoordinasikan seluruh Majelis Pimpinan Cabang (MPC) di wilayah Jawa Barat.
                </p>
                <p>
                  Sebagai perpanjangan tangan dari Majelis Pimpinan Nasional, MPW bertugas memastikan seluruh program kerja, visi, dan misi organisasi berjalan selaras dengan konstitusi dan AD/ART Pemuda Pancasila di tingkat daerah.
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-border/50 space-y-6">
              {keyRoles.map((role, i) => <div data-index={i} key={i} className="flex items-start gap-4">
                  <div className="mt-1 bg-primary/10 p-2 rounded-md shrink-0">
                    {role.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold font-default text-foreground mb-1">
                      {role.title}
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {role.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="bg-muted/20 border-dashed border-2 border-border/80 shadow-sm overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center min-h-[450px] relative z-10">
                <div className="bg-background p-4 rounded-lg shadow-sm border border-border/50 mb-6">
                  <Network className="size-10 text-muted-foreground/70" />
                </div>
                <h4 className="text-xl font-semibold font-default text-foreground mb-3">
                  Bagan Struktur Organisasi
                </h4>
                <p className="text-muted-foreground max-w-md mb-10">
                  Ruang khusus untuk visualisasi data regional dan diagram hierarki kepengurusan resmi MPW Jawa Barat.
                </p>

                {}
                <div className="flex flex-col items-center w-full max-w-sm opacity-60 select-none pointer-events-none">
                  {}
                  <div className="h-12 w-40 bg-background rounded-md border border-border shadow-sm flex items-center justify-center text-sm font-medium text-muted-foreground">
                    Ketua MPW
                  </div>
                  
                  {}
                  <div className="w-px h-8 bg-border" />
                  
                  {}
                  <div className="w-64 h-px bg-border" />
                  
                  {}
                  <div className="flex justify-between w-64">
                    <div className="flex flex-col items-center">
                      <div className="w-px h-6 bg-border" />
                      <div className="h-10 w-28 bg-background rounded-md border border-border shadow-sm flex items-center justify-center text-xs font-medium text-muted-foreground">
                        Sekretaris
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-px h-6 bg-border" />
                      <div className="h-10 w-28 bg-background rounded-md border border-border shadow-sm flex items-center justify-center text-xs font-medium text-muted-foreground">
                        Bendahara
                      </div>
                    </div>
                  </div>
                  
                  {}
                  <div className="flex justify-between w-64 mt-2">
                    <div className="flex flex-col items-center w-28">
                      <div className="w-px h-6 bg-border" />
                      <div className="h-8 w-20 bg-muted/50 rounded-md border border-border/50" />
                    </div>
                    <div className="flex flex-col items-center w-28">
                      <div className="w-px h-6 bg-border" />
                      <div className="h-8 w-20 bg-muted/50 rounded-md border border-border/50" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}